// ═══════════════════════════════════════════════════════════════════════
// Temps › À traiter (agent H ; conception § 14.2, principe P7).
// UNE boîte : congés, permissions, missions, récupérations, corrections, justifications,
// anomalies, dépassements d'horaire. Les journées normales n'y apparaissent jamais.
// Deux verbes : « Accepter » (un clic, sans fenêtre) et « Refuser » (motif obligatoire). Le serveur
// sait à quelle étape on se trouve. L'aide à la décision est dans la ligne. Sélection multiple pour
// les cas simples. Une anomalie se règle par la liste contrôlée renvoyée par le serveur (D-26).
// ═══════════════════════════════════════════════════════════════════════
import { useCallback, useEffect, useState } from 'react';
import { Inbox, Check, X, Send, Eye, CheckCheck, CalendarDays } from 'lucide-react';
import Layout from '../../components/Layout';
import { EnTetePage, Carte, Bouton, Badge, BandeauInfo, EtatVide, Squelette, Pagination } from '../../design/composants';
import JourneeDetailModal from './JourneeDetailModal';
import {
  NavEquipe, lireEquipe, ecrireEquipe, messageErreur, codeErreur, signalerATraiter, useDroitsEquipe, useReferentielATraiter,
  FenetreMotif, FenetreQualifier, FenetreArbitrer,
} from './EquipeCommun';
// Suivi « prochain acteur » (champ `suivi` des lignes, vue gestion) : qui doit agir et depuis quand.
import { LigneSuivi } from '../../components/suivi/SuiviDemande';

const TONS_TYPE = { CONGE: 'succes', AUTORISATION: 'info', DEMANDE_TEMPS: 'info', ANOMALIE: 'alerte', HEURES_SUP: 'neutre' };
const FILTRES = [
  { cle: '', libelle: 'Tout' },
  { cle: 'CONGE', libelle: 'Congés' },
  { cle: 'AUTORISATION', libelle: 'Permissions et missions' },
  { cle: 'DEMANDE_TEMPS', libelle: 'Régularisations' },
  { cle: 'ANOMALIE', libelle: 'Pointages' },
  { cle: 'HEURES_SUP', libelle: 'Heures sup.' },
];

function LigneATraiter({ l, selection, basculer, occupe, onAccepter, onFenetre }) {
  const simple = l.simple && l.actions.includes('ACCEPTER') && !l.motif_acceptation;
  return (
    <li style={{ alignItems: 'flex-start' }}>
      <span style={{ width: 22, paddingTop: 2 }}>
        {simple && <input type="checkbox" aria-label={`Sélectionner : ${l.libelle}`} checked={selection.has(l.cle)} onChange={() => basculer(l.cle)} />}
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <strong style={{ fontWeight: 600 }}>{l.libelle}</strong>
          <Badge ton={TONS_TYPE[l.type] || 'neutre'}>{l.categorie}</Badge>
        </span>
        <span className="rh-cellule-secondaire" style={{ display: 'block' }}>{l.detail}</span>
        <LigneSuivi suivi={l.suivi} />
        {(l.aide || []).length > 0 && (
          <ul style={{ margin: '4px 0 0', padding: 0, listStyle: 'none', fontSize: 13, color: 'var(--sg-texte-2)' }}>
            {l.aide.map((a) => <li key={a} style={{ padding: '1px 0', border: 0 }}>{a}</li>)}
          </ul>
        )}
      </span>
      <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
        {l.actions.includes('ACCEPTER') && !l.qualifications
          ? <Bouton petit variante="primaire" Icone={Check} disabled={occupe} onClick={() => (l.motif_acceptation ? onFenetre('accepter-motif', l) : onAccepter(l))}>{(l.libelles_actions || {}).ACCEPTER || 'Accepter'}</Bouton>
          : null}
        {l.actions.includes('ACCEPTER') && l.qualifications && (
          <Bouton petit variante="primaire" Icone={Check} disabled={occupe} onClick={() => onFenetre('accepter-qualifier', l)}>Accepter…</Bouton>
        )}
        {l.actions.includes('QUALIFIER') && <Bouton petit variante="primaire" Icone={Check} disabled={occupe} onClick={() => onFenetre('qualifier', l)}>Régler…</Bouton>}
        {l.actions.includes('ARBITRER') && <Bouton petit variante="primaire" Icone={Check} disabled={occupe} onClick={() => onFenetre('arbitrer', l)}>Décider…</Bouton>}
        {l.actions.includes('REFUSER') && <Bouton petit Icone={X} disabled={occupe} onClick={() => onFenetre('refuser', l)}>{(l.libelles_actions || {}).REFUSER || 'Refuser'}</Bouton>}
        {l.actions.includes('ESCALADER') && <Bouton petit variante="discret" Icone={Send} disabled={occupe} onClick={() => onFenetre('escalader', l)}>Confier à la RH</Bouton>}
        {['ANOMALIE', 'DEMANDE_TEMPS', 'HEURES_SUP'].includes(l.type) && l.date && (
          <Bouton petit variante="discret" Icone={Eye} onClick={() => onFenetre('journee', l)} aria-label={`Voir la journée : ${l.libelle}`}>Journée</Bouton>
        )}
      </span>
    </li>
  );
}

export default function ATraiterPage() {
  const droits = useDroitsEquipe();
  const referentiel = useReferentielATraiter();
  const [filtre, setFiltre] = useState('');
  const [page, setPage] = useState(1);
  const [taille, setTaille] = useState(25);
  const [d, setD] = useState(null);
  const [erreur, setErreur] = useState('');
  const [refus, setRefus] = useState(false);
  const [message, setMessage] = useState('');
  const [occupe, setOccupe] = useState(false);
  const [selection, setSelection] = useState(new Set());
  const [fenetre, setFenetre] = useState(null);

  const charger = useCallback(async () => {
    try {
      setErreur(''); setRefus(false);
      const r = await lireEquipe('/a-traiter', { page, taille, type: filtre || undefined });
      setD(r);
      setSelection((s) => new Set([...s].filter((cle) => r.lignes.some((l) => l.cle === cle))));
    } catch (e) {
      setD(null);
      if (e?.response?.status === 403) setRefus(codeErreur(e) || true);
      setErreur(messageErreur(e, 'Chargement impossible.'));
    }
  }, [page, taille, filtre]);
  useEffect(() => { charger(); }, [charger]);

  const apres = async (texte) => { setFenetre(null); setMessage(texte); signalerATraiter(); await charger(); };
  const basculer = (cle) => setSelection((s) => { const n = new Set(s); if (n.has(cle)) n.delete(cle); else n.add(cle); return n; });
  const url = (l, verbe) => `/a-traiter/${l.type}/${l.id}/${verbe}`;

  const accepter = async (l) => {
    setOccupe(true); setErreur(''); setMessage('');
    try { const r = await ecrireEquipe(url(l, 'accepter')); await apres(r.message || `Accepté : ${l.libelle}.`); } catch (e) { setErreur(messageErreur(e)); } finally { setOccupe(false); }
  };
  const accepterSelection = async () => {
    const lignes = (d?.lignes || []).filter((l) => selection.has(l.cle));
    if (!lignes.length) return;
    setOccupe(true); setErreur(''); setMessage('');
    try {
      const r = await ecrireEquipe('/a-traiter/accepter', { elements: lignes.map((l) => ({ type: l.type, id: l.id })) });
      setSelection(new Set());
      const echecs = r.resultats.filter((x) => !x.ok);
      await apres(`${r.acceptes} accepté${r.acceptes > 1 ? 's' : ''}.`);
      if (echecs.length) setErreur(`${echecs.length} non traité${echecs.length > 1 ? 's' : ''} : ${echecs.map((x) => x.message).join(' ; ')}`);
    } catch (e) { setErreur(messageErreur(e)); } finally { setOccupe(false); }
  };

  const simples = (d?.lignes || []).filter((l) => l.simple && l.actions.includes('ACCEPTER') && !l.motif_acceptation);
  const tousCoches = simples.length > 0 && simples.every((l) => selection.has(l.cle));
  const nb = (cle) => (cle ? (d?.par_type?.[cle] || 0) : (d?.total_general ?? d?.total ?? 0));

  return (
    <Layout>
      <div className="rh-page">
        <EnTetePage fil={[{ libelle: 'Temps' }, { libelle: 'À traiter' }]} titre={d ? `À traiter (${d.total_general ?? d.total})` : 'À traiter'}
          sousTitre="Les journées normales passent seules : vous ne voyez ici que ce qui demande votre décision." />
        <NavEquipe actif="a-traiter" compteur={droits?.compteur_a_traiter} />

        {refus && <EtatVide Icone={Inbox} titre="Aucune boîte à traiter" texte={erreur} />}
        {!refus && erreur && <BandeauInfo ton="danger">{erreur}</BandeauInfo>}
        {message && <BandeauInfo ton="succes">{message}</BandeauInfo>}

        {!refus && (
          <Carte>
            <div className="rh-vues" role="group" aria-label="Filtrer par type" style={{ marginBottom: 12, flexWrap: 'wrap' }}>
              {FILTRES.filter((f) => !f.cle || nb(f.cle) > 0 || filtre === f.cle).map((f) => (
                <button key={f.cle || 'tout'} type="button" className="rh-vue" aria-pressed={filtre === f.cle} onClick={() => { setFiltre(f.cle); setPage(1); }}>
                  {f.libelle}<span className="rh-vue-compte">{nb(f.cle)}</span>
                </button>
              ))}
            </div>
            {simples.length > 0 && (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
                <label style={{ display: 'inline-flex', gap: 6, alignItems: 'center', fontSize: 13 }}>
                  <input type="checkbox" checked={tousCoches} onChange={() => setSelection(tousCoches ? new Set() : new Set(simples.map((l) => l.cle)))} />
                  Tout sélectionner (cas simples)
                </label>
                <Bouton petit variante="primaire" Icone={CheckCheck} disabled={!selection.size || occupe} onClick={accepterSelection}>
                  Accepter la sélection{selection.size ? ` (${selection.size})` : ''}
                </Bouton>
              </div>
            )}
            {!d ? <><Squelette largeur="70%" /><Squelette largeur="50%" style={{ marginTop: 8 }} /></> : d.lignes.length === 0 ? (
              <EtatVide Icone={CalendarDays} titre="Rien à traiter" texte="Aucune demande ni anomalie n'attend votre décision." />
            ) : (
              <ul className="rh-liste">
                {d.lignes.map((l) => (
                  <LigneATraiter key={l.cle} l={l} selection={selection} basculer={basculer} occupe={occupe} onAccepter={accepter}
                    onFenetre={(mode, ligne) => setFenetre({ mode, ligne })} />
                ))}
              </ul>
            )}
            {d && <Pagination page={d.page} pages={d.pages} total={d.total} taille={d.taille} onPage={setPage} onTaille={(t) => { setTaille(t); setPage(1); }} tailles={[10, 25, 50]} />}
          </Carte>
        )}

        {fenetre?.mode === 'refuser' && (
          <FenetreMotif titre={`Refuser — ${fenetre.ligne.libelle}`} libelleBouton={(fenetre.ligne.libelles_actions || {}).REFUSER || 'Refuser'} onFermer={() => setFenetre(null)}
            onValider={async (motif) => { await ecrireEquipe(url(fenetre.ligne, 'refuser'), { motif }); await apres('Refusé. Le salarié est prévenu, avec le motif.'); }} />
        )}
        {fenetre?.mode === 'accepter-motif' && (
          <FenetreMotif titre={`Accepter — ${fenetre.ligne.libelle}`} libelleBouton="Accepter" variante="primaire" Icone={Check} aide="Motif obligatoire pour cette décision."
            onFermer={() => setFenetre(null)}
            onValider={async (motif) => { await ecrireEquipe(url(fenetre.ligne, 'accepter'), { motif }); await apres('Accepté.'); }} />
        )}
        {fenetre?.mode === 'escalader' && (
          <FenetreMotif titre={`Confier à la RH — ${fenetre.ligne.libelle}`} libelleBouton="Confier à la RH" variante="primaire" Icone={Send}
            aide="Expliquez pourquoi la RH doit décider." onFermer={() => setFenetre(null)}
            onValider={async (motif) => { await ecrireEquipe(url(fenetre.ligne, 'escalader'), { motif }); await apres('Confié à la RH.'); }} />
        )}
        {fenetre?.mode === 'qualifier' && (
          <FenetreQualifier ligne={fenetre.ligne} referentiel={referentiel} onFermer={() => setFenetre(null)}
            onValider={async (corps) => { await ecrireEquipe(url(fenetre.ligne, 'qualifier'), corps); await apres('Anomalie réglée.'); }} />
        )}
        {fenetre?.mode === 'accepter-qualifier' && (
          <FenetreQualifier ligne={fenetre.ligne} referentiel={referentiel} titre="Accepter" onFermer={() => setFenetre(null)}
            onValider={async (corps) => { await ecrireEquipe(url(fenetre.ligne, 'accepter'), { ...corps, commentaire: corps.motif }); await apres('Justification acceptée.'); }} />
        )}
        {fenetre?.mode === 'arbitrer' && (
          <FenetreArbitrer ligne={fenetre.ligne} onFermer={() => setFenetre(null)}
            onValider={async (corps) => { await ecrireEquipe(url(fenetre.ligne, 'arbitrer'), corps); await apres('Décision enregistrée.'); }} />
        )}
        {fenetre?.mode === 'journee' && (
          <JourneeDetailModal employeId={fenetre.ligne.salarie.id} date={fenetre.ligne.date} onFermer={() => { setFenetre(null); charger(); }} />
        )}
      </div>
    </Layout>
  );
}
